import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { validateEventCode } from '@crossmatch/shared';
import { authApi } from '../api/auth.js';
import { demoApi } from '../api/demo.js';
import { eventsApi } from '../api/events.js';
import { feedbackApi } from '../api/feedback.js';
import { meApi } from '../api/me.js';
import { toUserMessage } from '../api/errors.js';
import type { EventDto } from '../api/types.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { Loading } from '../components/Loading.js';
import { downloadJsonFile } from '../lib/download.js';
import { fileStamp, formatDateTime, schoolFullLabel } from '../lib/format.js';
import { userRoleLabel } from '../lib/labels.js';
import { qk } from '../lib/queryKeys.js';
import { useDemoStatusQuery, useMeQuery } from '../hooks/queries.js';
import { useSession } from '../store/session.js';
import { toast } from '../store/toast.js';

/** 设置与合规：导出我的数据（GET /me/export）、删除账号（DELETE /me，二次确认 + 输入确认词）。 */
export function SettingsPage() {
  const meQuery = useMeQuery();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clear = useSession((state) => state.clear);
  const refreshToken = useSession((state) => state.refreshToken);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // 修改密码表单
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  /** 自助改密：成功后后端已撤销全部会话（含当前设备），故本地登出并跳登录页 */
  const changePassword = useMutation({
    mutationFn: () => authApi.changePassword(oldPassword, newPassword),
    onSuccess: () => {
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError(null);
      clear();
      queryClient.clear();
      toast.success('密码已修改', '为安全起见，所有设备已退出，请用新密码重新登录');
      navigate('/login', { replace: true });
    },
    onError: (error) => {
      setPasswordError(toUserMessage(error));
    },
  });

  const exportData = useMutation({
    mutationFn: () => meApi.exportData(),
    onSuccess: (data) => {
      const filename = `crossmatch-export-${fileStamp()}.json`;
      downloadJsonFile(filename, data);
      toast.success('已导出我的数据', `文件名：${filename}（JSON，可用记事本或浏览器打开）`);
    },
  });

  const deleteAccount = useMutation({
    mutationFn: () => meApi.deleteAccount(),
    onSuccess: () => {
      setDeleteOpen(false);
      clear();
      queryClient.clear();
      toast.info('账号已删除', '本地会话已清空；后端已撤销登录凭据');
      navigate('/login', { replace: true });
    },
  });

  const demoStatus = useDemoStatusQuery();

  /** 载入演示数据（POST /demo/load，幂等）；成功后刷新角标与候选人列表 */
  const loadDemo = useMutation({
    mutationFn: () => demoApi.load(),
    onSuccess: (data) => {
      toast.success(
        `已载入 ${data.loaded} 位演示候选人`,
        '去「候选人池」就能看到；它们都带 demo- 前缀，讲完可以一键清除',
      );
      void queryClient.invalidateQueries({ queryKey: qk.demoStatus() });
      void queryClient.invalidateQueries({ queryKey: qk.candidatesRoot() });
    },
  });

  /** 清除演示数据（DELETE /demo，只删 demo- 前缀） */
  const clearDemo = useMutation({
    mutationFn: () => demoApi.clear(),
    onSuccess: (data) => {
      toast.success(
        data.removed > 0 ? `已清除 ${data.removed} 条演示数据` : '当前没有演示数据需要清除',
        '你自己录入的候选人不受影响',
      );
      void queryClient.invalidateQueries({ queryKey: qk.demoStatus() });
      void queryClient.invalidateQueries({ queryKey: qk.candidatesRoot() });
    },
  });

  const demoCount = demoStatus.data?.demoCandidates ?? 0;
  const demoBusy = loadDemo.isPending || clearDemo.isPending;

  /** 意见反馈：类型标签 + 内容（后端只收 content，标签以 [标签] 前缀写入内容） */
  const [feedbackTag, setFeedbackTag] = useState('建议');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const submitFeedback = useMutation({
    mutationFn: (content: string) => feedbackApi.submit(content),
    onSuccess: () => {
      setFeedbackText('');
      setFeedbackError(null);
      toast.success('反馈已提交', '谢谢！我们会按类型集中处理；内容已写入服务端 feedback 表');
    },
  });

  /** 组织者建赛事：成功后在卡片里展示赛事码并支持一键复制 */
  const [eventCode, setEventCode] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventSeason, setEventSeason] = useState('');
  const [eventError, setEventError] = useState<string | null>(null);
  const [createdEvent, setCreatedEvent] = useState<EventDto | null>(null);

  const createEvent = useMutation({
    mutationFn: () => eventsApi.create({ code: eventCode.trim(), name: eventName.trim(), season: eventSeason.trim() }),
    onSuccess: (data) => {
      setCreatedEvent(data.event);
      setEventError(null);
      toast.success(`赛事已创建：${data.event.code}`, '把赛事码发给参赛同学，他们在匹配/组队页填入即可进入同一赛事池');
    },
  });

  /** 提交反馈：内容非空校验；类型标签以 [标签] 前缀写入（后端只收 content） */
  const handleFeedbackSubmit = (): void => {
    const text = feedbackText.trim();
    if (text.length === 0) {
      setFeedbackError('请先填写反馈内容（1–500 字）');
      return;
    }
    if (text.length > 500) {
      setFeedbackError(`内容过长（当前 ${text.length} 字，上限 500 字）`);
      return;
    }
    setFeedbackError(null);
    submitFeedback.mutate(`[${feedbackTag}] ${text}`);
  };

  /** 建赛事：用 shared 的 validateEventCode 做与后端一致的赛事码校验 */
  const handleCreateEventSubmit = (): void => {
    const codeCheck = validateEventCode(eventCode);
    if (!codeCheck.ok) {
      setEventError(codeCheck.issues[0]?.message ?? '赛事码不合法');
      return;
    }
    if (eventName.trim().length === 0) {
      setEventError('请填写赛事名称');
      return;
    }
    if (eventSeason.trim().length === 0) {
      setEventError('请填写赛季（例如 2026春）');
      return;
    }
    setEventError(null);
    createEvent.mutate();
  };

  /** 复制赛事码：优先 clipboard，失败则选中文本让用户手动复制（不弹 alert） */
  const handleCopyCode = (code: string): void => {
    const clipboard = navigator.clipboard;
    if (clipboard && typeof clipboard.writeText === 'function') {
      void clipboard
        .writeText(code)
        .then(() => toast.success('赛事码已复制', code))
        .catch(() => toast.info('浏览器不允许自动复制', `请手动选择赛事码：${code}`));
      return;
    }
    toast.info('当前环境不支持自动复制', `请手动选择赛事码：${code}`);
  };

  const handleLogout = (): void => {
    if (refreshToken) {
      // 尽力撤销服务端 refresh token；失败不阻塞本地登出
      void authApi.logout(refreshToken).catch(() => undefined);
    }
    clear();
    queryClient.clear();
    toast.info('已退出登录', '本地会话已清空');
    navigate('/login', { replace: true });
  };

  if (meQuery.isPending) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">设置与合规</h1>
        </div>
        <Loading label="正在读取账号信息…" rows={3} />
      </div>
    );
  }

  if (meQuery.isError) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">设置与合规</h1>
        </div>
        <ErrorState error={meQuery.error} title="读取账号信息失败" onRetry={() => void meQuery.refetch()} />
      </div>
    );
  }

  const account = meQuery.data?.user;
  /** 建赛事需要 organizer/admin（与后端 requireRole('organizer','admin') 对齐） */
  const canManageEvents = account?.role === 'organizer' || account?.role === 'admin';

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">设置与合规</h1>
          <p className="page-sub">
            数据最小化：只存邮箱、昵称、学校、画像与组队数据；不收集学号、手机号、身份证。你随时可以导出或删除。
          </p>
        </div>
      </div>

      {!account ? (
        <EmptyState
          icon="⚿"
          title="读不到账号信息"
          description="可能是登录状态已失效。请重新登录后再查看设置。"
          action={
            <Button variant="primary" onClick={() => navigate('/login', { replace: true })}>
              去登录
            </Button>
          }
        />
      ) : (
        <>
          <Card title="账号信息" subtitle="账号与角色概览。">
            <div className="kv">
              <div className="kv-row">
                <span className="kv-key">昵称</span>
                <span className="kv-val">{account.name}</span>
              </div>
              <div className="kv-row">
                <span className="kv-key">邮箱</span>
                <span className="kv-val">{account.email}</span>
              </div>
              <div className="kv-row">
                <span className="kv-key">学校</span>
                <span className="kv-val">{schoolFullLabel(account.school)}</span>
              </div>
              <div className="kv-row">
                <span className="kv-key">角色</span>
                <span className="kv-val">{userRoleLabel(account.role)}</span>
              </div>
              {account.createdAt ? (
                <div className="kv-row">
                  <span className="kv-key">注册时间</span>
                  <span className="kv-val">{formatDateTime(account.createdAt)}</span>
                </div>
              ) : null}
              <div className="kv-row">
                <span className="kv-key">画像</span>
                <span className="kv-val">
                  {meQuery.data.profile
                    ? `${meQuery.data.profile.skills.length} 个技能 · ${meQuery.data.profile.weeklyHours} 小时/周`
                    : '还没有填写（去「我的画像」补全）'}
                </span>
              </div>
            </div>
            <p className="field-hint">
              会话说明：accessToken 只存在内存（刷新页面即失效，会自动用 refreshToken 换新）；refreshToken 存在本机
              localStorage，退出登录或删除账号时会被清除。
            </p>
            <div className="row">
              <Button variant="ghost" onClick={handleLogout}>
                退出登录
              </Button>
              <Button variant="secondary" onClick={() => navigate('/profile')}>
                去编辑画像
              </Button>
            </div>
          </Card>

          <Card title="修改密码" subtitle="验证旧密码后设置新密码；修改成功后所有设备需重新登录。忘了密码可用登录页的「忘记密码」找回。">
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                setPasswordError(null);
                if (newPassword.length < 8 || newPassword.length > 72) {
                  setPasswordError('新密码至少 8 位（上限 72 位）');
                  return;
                }
                if (newPassword !== confirmPassword) {
                  setPasswordError('两次输入的新密码不一致');
                  return;
                }
                changePassword.mutate();
              }}
              noValidate
            >
              {passwordError !== null ? (
                <div className="banner banner-danger" role="alert">
                  <span className="banner-ico" aria-hidden="true">
                    !
                  </span>
                  <span className="wrap-anywhere">{passwordError}</span>
                </div>
              ) : null}

              <div className="field">
                <label className="field-label" htmlFor="settings-old-password">
                  当前密码
                </label>
                <input
                  id="settings-old-password"
                  className="input"
                  type="password"
                  value={oldPassword}
                  autoComplete="current-password"
                  placeholder="当前密码"
                  onChange={(event) => setOldPassword(event.target.value)}
                />
              </div>

              <div className="field">
                <label className="field-label" htmlFor="settings-new-password">
                  新密码
                </label>
                <input
                  id="settings-new-password"
                  className="input"
                  type="password"
                  value={newPassword}
                  autoComplete="new-password"
                  placeholder="至少 8 位"
                  onChange={(event) => setNewPassword(event.target.value)}
                />
              </div>

              <div className="field">
                <label className="field-label" htmlFor="settings-confirm-password">
                  确认新密码
                </label>
                <input
                  id="settings-confirm-password"
                  className="input"
                  type="password"
                  value={confirmPassword}
                  autoComplete="new-password"
                  placeholder="再输入一次"
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </div>

              <div className="row">
                <Button type="submit" variant="primary" loading={changePassword.isPending}>
                  修改密码
                </Button>
              </div>
            </form>
          </Card>

          <Card title="导出我的数据" subtitle="一键导出账号、画像、技能、队伍、待办等全部数据（JSON）。">
            <div className="row">
              <Button variant="primary" loading={exportData.isPending} onClick={() => exportData.mutate()}>
                {exportData.isPending ? '正在打包…' : '导出 JSON'}
              </Button>
              <span className="field-hint">导出为本地文件，不上传到任何第三方；适合答辩演示"数据可携带"。</span>
            </div>
            {exportData.isError ? (
              <div className="banner banner-danger" role="alert">
                <span className="banner-ico" aria-hidden="true">
                  !
                </span>
                <span className="wrap-anywhere">导出失败：{toUserMessage(exportData.error)}</span>
              </div>
            ) : null}
          </Card>

          <Card
            title="演示模式"
            subtitle="答辩演示一键铺数据、讲完一键清干净；只影响 demo- 前缀数据。"
          >
            <p className="field-hint">
              「清除演示数据」不影响你自己录入的候选人；重复载入不会产生重复数据。
            </p>
            <div className="row">
              <Button variant="primary" loading={loadDemo.isPending} disabled={demoBusy && !loadDemo.isPending} onClick={() => loadDemo.mutate()}>
                {loadDemo.isPending ? '正在载入…' : '载入演示数据'}
              </Button>
              <Button
                variant="secondary"
                loading={clearDemo.isPending}
                disabled={demoBusy && !clearDemo.isPending}
                onClick={() => clearDemo.mutate()}
              >
                {clearDemo.isPending ? '正在清除…' : '清除演示数据'}
              </Button>
            </div>
            <p className="field-hint" role="status">
              {demoStatus.isPending
                ? '正在读取演示数据条数…'
                : demoStatus.isError
                  ? '演示数据条数暂时读不到（不影响其它功能；可在候选人池按 demo- 前缀自行确认）'
                  : demoCount > 0
                    ? `当前有 ${demoCount} 条演示候选人，左侧/顶部角标显示「演示数据」。`
                    : '当前没有演示数据，角标显示「我的数据」。'}
            </p>
            {loadDemo.isError || clearDemo.isError ? (
              <div className="banner banner-danger" role="alert">
                <span className="banner-ico" aria-hidden="true">
                  !
                </span>
                <span className="wrap-anywhere">
                  {loadDemo.isError ? `载入演示数据失败：${toUserMessage(loadDemo.error)}` : ''}
                  {loadDemo.isError && clearDemo.isError ? ' / ' : ''}
                  {clearDemo.isError ? `清除演示数据失败：${toUserMessage(clearDemo.error)}` : ''}
                </span>
              </div>
            ) : null}
          </Card>

          <Card
            title="意见反馈"
            subtitle="把使用中的问题或建议留给开发团队。"
          >
            <p className="field-hint">
              后端该接口只接收 <code className="mono">content</code> 一个字段，因此「类型」会以
              <code className="mono">[建议]</code> 形式写在内容开头——不新增后端不认的字段，也不会丢掉你选的类型。
            </p>
            <label className="field-label" htmlFor="feedback-tag">
              类型
            </label>
            <select
              id="feedback-tag"
              className="input"
              value={feedbackTag}
              onChange={(event) => setFeedbackTag(event.target.value)}
            >
              <option value="建议">建议</option>
              <option value="问题">问题</option>
              <option value="其他">其他</option>
            </select>

            <label className="field-label" htmlFor="feedback-content">
              内容
            </label>
            <textarea
              id="feedback-content"
              className="textarea"
              rows={4}
              maxLength={500}
              placeholder="例如：在「开始匹配」里填完赛事码后，希望候选池能按学校再分组一次。"
              value={feedbackText}
              onChange={(event) => {
                setFeedbackText(event.target.value);
                if (feedbackError !== null) setFeedbackError(null);
              }}
            />
            <span className="field-hint" role="status">
              已输入 {feedbackText.trim().length}/500 字
              {feedbackText.trim().length > 0 ? `；提交内容会带上前缀 ${`[${feedbackTag}]`}` : ''}
            </span>

            <div className="row">
              <Button variant="primary" loading={submitFeedback.isPending} onClick={handleFeedbackSubmit}>
                {submitFeedback.isPending ? '正在提交…' : '提交反馈'}
              </Button>
              <Button
                variant="ghost"
                disabled={submitFeedback.isPending || feedbackText.trim().length === 0}
                onClick={() => {
                  setFeedbackText('');
                  setFeedbackError(null);
                }}
              >
                清空
              </Button>
            </div>

            {feedbackError !== null ? (
              <div className="banner banner-danger" role="alert">
                <span className="banner-ico" aria-hidden="true">
                  !
                </span>
                <span className="wrap-anywhere">{feedbackError}</span>
              </div>
            ) : null}
            {submitFeedback.isError ? (
              <div className="banner banner-danger" role="alert">
                <span className="banner-ico" aria-hidden="true">
                  !
                </span>
                <span className="wrap-anywhere">提交反馈失败：{toUserMessage(submitFeedback.error)}</span>
              </div>
            ) : null}
            {submitFeedback.isSuccess ? (
              <div className="banner" role="status">
                <span className="banner-ico" aria-hidden="true">
                  ✓
                </span>
                <span className="wrap-anywhere">已收到（记录 id：{submitFeedback.data.id}）。可继续提交下一条。</span>
              </div>
            ) : null}
          </Card>

          <Card
            title="我的赛事"
            subtitle="创建赛事并拿到赛事码（仅 organizer / admin）。"
          >
            {!canManageEvents ? (
              <div className="banner banner-warn" role="note">
                <span className="banner-ico" aria-hidden="true">
                  !
                </span>
                <div>
                  <strong>需要组织者权限。</strong>
                  <div className="small">
                    当前账号角色是「{userRoleLabel(account.role)}」。若你是赛事负责人，请联系管理员调整角色；
                    参赛同学直接用赛事方发来的赛事码，在「开始匹配」「我的队伍」里填入即可。
                  </div>
                </div>
              </div>
            ) : (
              <>
                <label className="field-label" htmlFor="event-code">
                  赛事码（6–12 位大写字母或数字）
                </label>
                <input
                  id="event-code"
                  className="input"
                  value={eventCode}
                  maxLength={12}
                  placeholder="例如 SJ2026A"
                  onChange={(event) => {
                    setEventCode(event.target.value.toUpperCase());
                    if (eventError !== null) setEventError(null);
                  }}
                />
                <label className="field-label" htmlFor="event-name">
                  赛事名称
                </label>
                <input
                  id="event-name"
                  className="input"
                  value={eventName}
                  maxLength={60}
                  placeholder="例如 松江七校创新创业挑战赛"
                  onChange={(event) => {
                    setEventName(event.target.value);
                    if (eventError !== null) setEventError(null);
                  }}
                />
                <label className="field-label" htmlFor="event-season">
                  赛季
                </label>
                <input
                  id="event-season"
                  className="input"
                  value={eventSeason}
                  maxLength={20}
                  placeholder="例如 2026春"
                  onChange={(event) => {
                    setEventSeason(event.target.value);
                    if (eventError !== null) setEventError(null);
                  }}
                />
                <span className="field-hint">
                  赛事码校验用的是 shared 的 <code className="mono">validateEventCode</code>，与后端同一套规则；重复的赛事码后端会返回 409。
                </span>
                <div className="row">
                  <Button variant="primary" loading={createEvent.isPending} onClick={handleCreateEventSubmit}>
                    {createEvent.isPending ? '正在创建…' : '创建赛事'}
                  </Button>
                </div>

                {eventError !== null ? (
                  <div className="banner banner-danger" role="alert">
                    <span className="banner-ico" aria-hidden="true">
                      !
                    </span>
                    <span className="wrap-anywhere">{eventError}</span>
                  </div>
                ) : null}
                {createEvent.isError ? (
                  <div className="banner banner-danger" role="alert">
                    <span className="banner-ico" aria-hidden="true">
                      !
                    </span>
                    <span className="wrap-anywhere">创建赛事失败：{toUserMessage(createEvent.error)}</span>
                  </div>
                ) : null}

                {createdEvent !== null ? (
                  <div className="kv">
                    <div className="kv-row">
                      <span className="kv-key">赛事码</span>
                      <span className="kv-val">
                        <span className="mono">{createdEvent.code}</span>
                      </span>
                    </div>
                    <div className="kv-row">
                      <span className="kv-key">名称</span>
                      <span className="kv-val">{createdEvent.name}</span>
                    </div>
                    <div className="kv-row">
                      <span className="kv-key">赛季 / 状态</span>
                      <span className="kv-val">
                        {createdEvent.season ?? '—'} · {createdEvent.status ?? '—'}
                      </span>
                    </div>
                    <div className="kv-row">
                      <span className="kv-key">赛事人数</span>
                      <span className="kv-val">
                        {typeof createdEvent.memberCount === 'number' ? `${createdEvent.memberCount} 人（含你自己）` : '—'}
                      </span>
                    </div>
                    <div className="row">
                      <Button variant="secondary" onClick={() => handleCopyCode(createdEvent.code)}>
                        复制赛事码
                      </Button>
                      <span className="field-hint">
                        把赛事码发给参赛同学，他们在「开始匹配」「我的队伍」里填入即进入同一赛事池。
                      </span>
                    </div>
                    <p className="muted small">
                      说明：后端目前只有「按赛事码查询」和「创建赛事」两个接口，没有"我创建的赛事列表"，所以这里只展示本次刚创建的赛事，刷新页面后需要重新创建或直接用赛事码查询。
                    </p>
                  </div>
                ) : null}
              </>
            )}
          </Card>

          <Card
            title="删除账号"
            subtitle="删除后无法登录，数据随账号一并不可访问。"
          >
            <div className="banner banner-danger" role="alert">
              <span className="banner-ico" aria-hidden="true">
                !
              </span>
              <div>
                <strong>这是不可恢复操作。</strong>
                <div className="small">
                  删除后：登录凭据立即失效、画像与队伍不再可用。若只是想换账号，请先用上面的「导出 JSON」保存数据。
                </div>
              </div>
            </div>
            <div className="row">
              <Button variant="danger" onClick={() => setDeleteOpen(true)}>
                删除我的账号
              </Button>
              <span className="field-hint">点击后会要求你输入确认词，避免误触。</span>
            </div>
            {deleteAccount.isError ? (
              <div className="banner banner-danger" role="alert">
                <span className="banner-ico" aria-hidden="true">
                  !
                </span>
                <span className="wrap-anywhere">删除账号失败：{toUserMessage(deleteAccount.error)}</span>
              </div>
            ) : null}
          </Card>
        </>
      )}

      <ConfirmDialog
        open={deleteOpen}
        danger
        title="删除账号？"
        requireText="删除账号"
        description={
          <>
            <p>账号将被标记删除并撤销登录令牌；画像、队伍、待办等数据随账号一并不可访问。</p>
            <p className="muted small">
              合规说明：为保证审计与防重复注册，服务端保留最小必要的删除标记；如需彻底清除请联系赛事管理员。
            </p>
          </>
        }
        confirmText="永久删除"
        pending={deleteAccount.isPending}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => deleteAccount.mutate()}
      />
    </div>
  );
}